// GtRoleRecord — the role record, in the same layout as the candidate and client
// records: a profile header with quick facts, actions and a stat strip, tabs on
// the left (Profile, JD, advert, rate cards, pipeline, submissions, notes) and key
// information, the public listing and the profile at a glance on the right.
// Everything inside .gtr2 is 12px. Candidates open in place from the pipeline.
import { useState, useEffect, useCallback } from 'react';
import GtLoader from '../../components/GtLoader';
import API_BASE from '../../config';
import { GtSelect } from '../../components/GtControls';
import { ClientField, ExpertiseField } from '../../components/GtTaxonomyFields';
import GtEditor from '../../components/GtEditor';
import GtSubmissions from './GtSubmissions';
import GtPipeline from './GtPipeline';
import GtCandidateRecord from './GtCandidateRecord';
import { Ico, ICONS, Stat } from '../../components/GtRecordKit';
import GtSourcingPanel from '../../components/GtSourcing';
import GtInterviewPlan from '../../components/GtInterviewPlan';

function initials(name) { return (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase(); }
const AVATAR_COLOURS = ['#1E5A7B', '#2E7D32', '#6A1B9A', '#B8860B', '#00695C', '#C2410C', '#374151', '#9D174D', '#1565C0', '#4E342E', '#5B6C1E', '#0F766E', '#B91C1C', '#5D4037', '#3949AB', '#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVATAR_COLOURS[h % AVATAR_COLOURS.length]; }

// Light tints for profile chips. A fixed palette (no orange, per house style),
// picked by a hash of the label so a given term always carries the same colour
// wherever it appears, rather than shifting between renders.
const CHIP_TONES = [
  { fg: '#17838A', bg: 'rgba(31,169,178,.12)', br: 'rgba(31,169,178,.26)' },
  { fg: '#068A63', bg: 'rgba(0,168,120,.12)',  br: 'rgba(0,168,120,.26)' },
  { fg: '#2563EB', bg: 'rgba(59,130,246,.11)', br: 'rgba(59,130,246,.24)' },
  { fg: '#6D28D9', bg: 'rgba(139,92,246,.11)', br: 'rgba(139,92,246,.24)' },
  { fg: '#B4740B', bg: 'rgba(245,166,35,.15)', br: 'rgba(245,166,35,.30)' },
  { fg: '#C81E3E', bg: 'rgba(244,63,94,.10)',  br: 'rgba(244,63,94,.22)' },
  { fg: '#0E7490', bg: 'rgba(14,116,144,.11)', br: 'rgba(14,116,144,.24)' },
  { fg: '#4F46E5', bg: 'rgba(79,70,229,.11)',  br: 'rgba(79,70,229,.24)' },
];
function chipTone(label) {
  const s = String(label || '?');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const t = CHIP_TONES[h % CHIP_TONES.length];
  return { color: t.fg, background: t.bg, borderColor: t.br };
}
function ukDate(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }

const ROLE_STATUS_PILL = { draft: 'gtt-p-slate', open: 'gtt-p-green', paused: 'gtt-p-amber', filled: 'gtt-p-blue', closed: 'gtt-p-slate' };
const STATUS_OPTS = [
  { value: 'draft', label: 'Draft' }, { value: 'open', label: 'Open' }, { value: 'paused', label: 'Paused' },
  { value: 'filled', label: 'Filled' }, { value: 'closed', label: 'Closed' },
];
const PLACEMENT_OPTS = [
  { value: 'permanent_fee', label: 'Permanent (fee)' }, { value: 'contract_rate_card', label: 'Contract (rate card)' },
];
const WORK_PATTERN_OPTS = [
  { value: '', label: '—' },
  { value: 'on-site', label: 'On-site' }, { value: 'hybrid', label: 'Hybrid' }, { value: 'remote', label: 'Remote' },
];
const RATE_UNIT_OPTS = [
  { value: 'hour', label: 'Per hour' }, { value: 'day', label: 'Per day' }, { value: 'week', label: 'Per week' },
  { value: 'month', label: 'Per month' }, { value: 'task', label: 'Per task' },
];
const TABS = ['Profile', 'Job description', 'Public advert', 'Rate cards', 'Matches', 'Interview', 'Pipeline', 'Submissions', 'Notes'];


// Role Profile is free-form JSONB; these are the keys the extractor produces.
const PROFILE_LIST_KEYS = [['skills', 'Skills'], ['stack', 'Stack'], ['must_haves', 'Must-haves'], ['nice_to_haves', 'Nice-to-haves']];
const PROFILE_TEXT_KEYS = [['seniority', 'Seniority'], ['location', 'Location'], ['comp_band', 'Comp band']];

export default function GtRoleRecord({ roleId, getToken, onBack, userName, userImage, backLabel, initialCandidateId, onOpenCandidate, onCloseCandidate }) {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('Profile');
  // The open nested candidate is the URL, passed down as initialCandidateId, so
  // it deep-links and survives refresh. Same rule as the client-to-role nesting:
  // one source of truth, no local copy to drift out of step.
  const openCandidateId = initialCandidateId || null;
  const setOpenCandidateId = (cid) => cid ? onOpenCandidate && onOpenCandidate(cid) : onCloseCandidate && onCloseCandidate();
  const [toast, setToast] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesHasMore, setNotesHasMore] = useState(false);
  const [notesCursor, setNotesCursor] = useState(null);
  const [notesLoadingMore, setNotesLoadingMore] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [myClerkId, setMyClerkId] = useState(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [jdEditing, setJdEditing] = useState(false);
  const [jdDraft, setJdDraft] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileDraft, setProfileDraft] = useState('');
  const [newRc, setNewRc] = useState({ band: '', rate_gbp: '', rate_unit: 'day', notes: '' });
  const [advertDraft, setAdvertDraft] = useState('');
  const [advertSeeded, setAdvertSeeded] = useState(false);
  const [advertSaving, setAdvertSaving] = useState(false);
  const [blurb, setBlurb] = useState('');
  const [blurbLoading, setBlurbLoading] = useState(false);
  const [blurbMsg, setBlurbMsg] = useState(null);
  const [pubFields, setPubFields] = useState({ public_location: '', public_salary_text: '', public_work_pattern: '' });
  const [pubSaving, setPubSaving] = useState(false);
  const [listing, setListing] = useState(false);
  const [listingBusy, setListingBusy] = useState(false);
  const [sourcing, setSourcing] = useState(null);

  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, { ...opts, headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || body.error || ('request failed: ' + res.status));
    return body;
  }, [getToken]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await authFetch('/api/roles/' + roleId);
      setRole(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [authFetch, roleId]);

  useEffect(() => {
    if (!role) return;
    // Show the client's stored blurb if one exists, rather than clearing it.
    // The client GET returns public_blurb; a fresh Generate overwrites this.
    if (role.client_id) {
      authFetch('/api/clients/' + role.client_id).then(c => setBlurb((c && c.public_blurb) || '')).catch(() => setBlurb(''));
    } else {
      setBlurb('');
    }
    setPubFields({
      public_location: role.public_location || '',
      public_salary_text: role.public_salary_text || '',
      public_work_pattern: role.public_work_pattern || '',
    });
    setListing(!!role.listed_publicly);
    // Seed the advert body: its own copy if it has diverged, otherwise the JD.
    setAdvertDraft(role.advert_html || role.jd_html || '');
    setAdvertSeeded(!role.advert_html && !!role.jd_html);
  }, [role, authFetch]);
  useEffect(() => { load(); }, [load]);
  // The Suggested tray's counts: waiting suggestions and bench fits.
  const roleDbId = role ? role.id : null;
  const loadSourcing = useCallback(() => {
    if (!roleDbId) return;
    authFetch('/api/sourcing/role/' + roleDbId).then(r => setSourcing(r.summary || null)).catch(() => {});
  }, [authFetch, roleDbId]);
  useEffect(() => { loadSourcing(); }, [loadSourcing]);
  useEffect(() => { loadNotes(); }, [roleId]); // eslint-disable-line react-hooks/exhaustive-deps

  function flash(msg) { setToast(msg); setTimeout(() => setToast(null), 2600); }




  const loadNotes = async () => {
    try {
      const np = await authFetch('/api/roles/' + roleId + '/notes?limit=20');
      setNotes(np.notes || []); setNotesHasMore(!!np.has_more); setNotesCursor(np.next_before || null);
    } catch (e) { /* non-fatal */ }
  };
  async function loadEarlierNotes() {
    if (!notesCursor || notesLoadingMore) return;
    setNotesLoadingMore(true);
    try {
      const np = await authFetch('/api/roles/' + roleId + '/notes?limit=20&before=' + encodeURIComponent(notesCursor));
      setNotes(prev => [...(np.notes || []), ...prev]); setNotesHasMore(!!np.has_more); setNotesCursor(np.next_before || null);
    } catch (e) { /* */ }
    setNotesLoadingMore(false);
  }
  async function postNote() {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const note = await authFetch('/api/roles/' + roleId + '/notes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body: newNote.trim(), author_name: userName || null }) });
      setMyClerkId(note.author_clerk_id); setNotes(prev => [...prev, note]); setNewNote('');
    } catch (e) { flash(e.message); }
    setSavingNote(false);
  }
  async function deleteNote(noteId) {
    try { await authFetch('/api/roles/' + roleId + '/notes/' + noteId, { method: 'DELETE' }); setNotes(prev => prev.filter(n => n.id !== noteId)); }
    catch (e) { flash(e.message); }
  }

  function startEdit() {
    setForm({
      title: role.title || '', client_id: role.client_id == null ? '' : String(role.client_id),
      client_contact_name: role.client_contact_name || '', client_contact_email: role.client_contact_email || '',
      expertise_domain: role.expertise_domain || 'engineering', status: role.status || 'open',
      placement_model: role.placement_model || 'permanent_fee',
      placement_fee_percent: role.placement_fee_percent == null ? '' : String(role.placement_fee_percent),
    });
    setEditing(true);
  }
  async function saveEdit() {
    try {
      const payload = { ...form };
      payload.placement_fee_percent = payload.placement_fee_percent === '' ? null : Number(payload.placement_fee_percent);
      const updated = await authFetch('/api/roles/' + roleId, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      setRole(r => ({ ...r, ...updated }));
      setEditing(false);
      flash('Role updated');
    } catch (e) { flash(e.message); }
  }

  async function saveJd() {
    try {
      const updated = await authFetch('/api/roles/' + roleId, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ jd_html: jdDraft }) });
      setRole(r => ({ ...r, ...updated }));
      setJdEditing(false);
      flash('Job description saved');
    } catch (e) { flash(e.message); }
  }

  // -- Public advert handlers ------------------------------------------------
  async function saveAdvert() {
    setAdvertSaving(true);
    try {
      const updated = await authFetch('/api/roles/' + roleId, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ advert_html: advertDraft }) });
      setRole(r => ({ ...r, ...updated }));
      flash('Advert saved');
    } catch (e) { flash(e.message); }
    finally { setAdvertSaving(false); }
  }

  async function generateBlurb() {
    if (!role.client_id) { flash('This role has no client attached.'); return; }
    setBlurbLoading(true);
    setBlurbMsg(null);
    try {
      const client = await authFetch('/api/clients/' + role.client_id + '/blurb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      setBlurb(client.public_blurb || '');
      flash('Client description generated');
    } catch (e) {
      setBlurbMsg(e.message || 'Generation failed');
    }
    finally { setBlurbLoading(false); }
  }

  async function savePublicFields() {
    setPubSaving(true);
    try {
      const updated = await authFetch('/api/roles/' + roleId, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pubFields) });
      setRole(r => ({ ...r, ...updated }));
      flash('Public details saved');
    } catch (e) { flash(e.message); }
    finally { setPubSaving(false); }
  }

  async function toggleListing(next) {
    setListingBusy(true);
    try {
      const updated = await authFetch('/api/roles/' + roleId + '/listing', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ listed: next }) });
      setRole(r => ({ ...r, ...updated }));
      setListing(!!updated.listed_publicly);
      flash(updated.listed_publicly ? 'Role is now listed publicly' : 'Role removed from public listing');
    } catch (e) { flash(e.message); }
    finally { setListingBusy(false); }
  }

  async function extractProfile() {
    setExtracting(true);
    try {
      const updated = await authFetch('/api/roles/' + roleId + '/extract-profile', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      setRole(r => ({ ...r, ...updated }));
      flash('Role Profile extracted — review it below');
      setTab('Profile');
    } catch (e) { flash(e.message); }
    finally { setExtracting(false); }
  }

  function startProfileEdit() {
    setProfileDraft(JSON.stringify(role.role_profile || {}, null, 2));
    setProfileEditing(true);
  }
  async function saveProfile() {
    let parsed;
    try { parsed = JSON.parse(profileDraft); }
    catch (e) { flash('Not valid JSON — check the structure'); return; }
    try {
      const updated = await authFetch('/api/roles/' + roleId + '/profile', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ role_profile: parsed }) });
      setRole(r => ({ ...r, ...updated }));
      setProfileEditing(false);
      flash('Role Profile saved');
    } catch (e) { flash(e.message); }
  }

  async function addRateCard() {
    if (!newRc.band.trim() || newRc.rate_gbp === '') { flash('Band and rate are required'); return; }
    try {
      await authFetch('/api/roles/' + roleId + '/rate-cards', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ band: newRc.band, rate_gbp: Number(newRc.rate_gbp), rate_unit: newRc.rate_unit, notes: newRc.notes || null }) });
      setNewRc({ band: '', rate_gbp: '', rate_unit: 'day', notes: '' });
      await load();
      flash('Rate card added');
    } catch (e) { flash(e.message); }
  }
  async function deleteRateCard(id) {
    try { await authFetch('/api/rate-cards/' + id, { method: 'DELETE' }); await load(); flash('Rate card removed'); }
    catch (e) { flash(e.message); }
  }

  if (openCandidateId) return <GtCandidateRecord candidateId={openCandidateId} getToken={getToken} userName={userName} userImage={userImage} backLabel={(role && role.title) || 'Role'} onBack={() => (onCloseCandidate ? onCloseCandidate() : null)} />;

  if (loading) return <div className="gtr2"><GtLoader /></div>;
  if (error) return <div className="gtr2"><div className="gtr2-top"><button type="button" className="gtr2-back" onClick={onBack}>← {backLabel || 'Roles'}</button></div><div className="gtc-error">{error}</div></div>;
  if (!role) return null;

  const nm = role.title || 'Untitled role';
  const rateCards = role.rate_cards || [];
  const profile = role.role_profile || null;

  const mustN = profile && Array.isArray(profile.must_haves) ? profile.must_haves.length : 0;
  const skillN = profile && Array.isArray(profile.skills) ? profile.skills.length : 0;
  const domainLabel = role.expertise_domain ? String(role.expertise_domain).replace(/_/g, ' ').replace(/^./, ch => ch.toUpperCase()) : null;
  const isContract = role.placement_model === 'contract_rate_card';
  const placementLabel = isContract ? 'Contract, rate card' : 'Permanent' + (role.placement_fee_percent != null ? ', ' + Number(role.placement_fee_percent) + '% fee' : '');
  const closedRole = role.status === 'filled' || role.status === 'closed';
  const tabCount = { Notes: notes.length, 'Rate cards': rateCards.length, Matches: sourcing ? sourcing.new : 0 };

  return (
    <div className="gtr2">
      <div className="gtr2-top">
        <button type="button" className="gtr2-back" onClick={onBack}><Ico d={ICONS.back} />{backLabel || 'Roles'}</button>
        <span className="gtr2-sep">/</span>
        <span className="gtr2-crumb">{nm}</span>
        {role.ref && <span className="gtr2-ref">{role.ref}</span>}
      </div>

      {toast && <div className="gtc-toast">{toast}</div>}

      <section className="gtr2-hero">
        <div className="gtr2-cover gtr2-cover--role" />
        <div className="gtr2-hero-row">
          <span className="gtr2-avatar gtr2-avatar--icon" style={{ background: avatarColour(nm) }}><Ico d={ICONS.brief} size={30} /></span>
          <div className="gtr2-id">
            <div className="gtr2-namerow">
              <h1 className="gtr2-name">{nm}</h1>
              <span className={'gtt-pill gtr2-status ' + (ROLE_STATUS_PILL[role.status] || 'gtt-p-slate')}>{role.status}</span>
            </div>
            <p className="gtr2-headline">
              {role.client_name ? <>For <b className="gtr2-at-b">{role.client_name}</b></> : 'No client attached'}
              {role.client_contact_name ? ' · hiring contact ' + role.client_contact_name : ''}
            </p>
            <div className="gtr2-chips">
              {domainLabel && <span className="gtr2-chip gtr2-chip--domain"><Ico d={ICONS.spark} />{domainLabel}</span>}
              <span className="gtr2-chip"><Ico d={ICONS.rate} />{placementLabel}</span>
              {profile && profile.seniority && <span className="gtr2-chip"><Ico d={ICONS.layers} />{String(profile.seniority)}</span>}
              {profile && profile.location && <span className="gtr2-chip"><Ico d={ICONS.pin} />{String(profile.location)}</span>}
              {profile && profile.comp_band && <span className="gtr2-chip"><Ico d={ICONS.brief} />{String(profile.comp_band)}</span>}
              {sourcing && sourcing.bench_fits > 0 && (
                <button type="button" className="gtr2-chip gtsr-fitchip" onClick={() => setTab('Matches')}>
                  <Ico d={ICONS.users} />{sourcing.bench_fits} {sourcing.bench_fits === 1 ? 'person' : 'people'} on your benches {sourcing.bench_fits === 1 ? 'fits' : 'fit'} this role
                </button>
              )}
              {listing && role.ref
                ? <span className="gtr2-chip gtr2-chip--live"><span className="gtr2-livedot" />Listed at jobs.gtco.io/{role.ref}</span>
                : <span className="gtr2-chip"><Ico d={ICONS.globe} />Not listed publicly</span>}
            </div>
          </div>
          <div className="gtr2-acts">
            <button type="button" className="gtr2-btn gtr2-btn--primary" onClick={() => setTab('Pipeline')}><Ico d={ICONS.board} />Pipeline</button>
            <button type="button" className="gtr2-btn" onClick={() => setTab('Job description')}><Ico d={ICONS.doc} />Job description</button>
            <button type="button" className="gtr2-btn" onClick={startEdit} disabled={editing}><Ico d={ICONS.edit} />Edit details</button>
          </div>
        </div>

        <div className="gtr2-stats">
          <Stat label="Must-haves" value={mustN} sub={profile ? skillN + ' skills' : 'No profile yet'} onClick={() => setTab('Profile')} />
          <Stat label="Placement" value={isContract ? 'Contract' : 'Permanent'} sub={!isContract && role.placement_fee_percent != null ? Number(role.placement_fee_percent) + '% fee' : null} />
          <Stat label="Rate cards" value={rateCards.length} onClick={() => setTab('Rate cards')} />
          <Stat label="Public listing" value={listing ? 'Listed' : 'Not listed'} onClick={() => setTab('Public advert')} />
          <Stat label="Notes" value={notes.length} onClick={() => setTab('Notes')} />
          <Stat label="Created" value={ukDate(role.created_at) || '—'} />
        </div>
      </section>

      <div className="gtr2-body">
        <div className="gtr2-main">
          <div className="gtr-card gtr2-tabcard">
            <div className="gtr2-tabs" role="tablist">
              {TABS.map(t => (
                <button type="button" role="tab" aria-selected={tab === t} key={t} className={'gtr2-tab' + (tab === t ? ' gtr2-tab--on' : '')} onClick={() => setTab(t)}>
                  {t}{tabCount[t] ? <span className="gtr2-count">{tabCount[t]}</span> : null}
                </button>
              ))}
            </div>

            <div className="gtr2-tabbody">
              {tab === 'Profile' && (
                <div className="gtr2-pad">
                  <div className="gtr2-tabtools">
                    <span>Structured requirements taken from the job description. Matching and interview plans read from this.</span>
                    <span className="gtr2-panelacts">
                      {role.jd_text ? <button className="gtr2-btn gtr2-btn--sm" disabled={extracting} onClick={extractProfile}><Ico d={ICONS.spark} />{extracting ? 'Extracting…' : (profile ? 'Re-extract' : 'Extract from JD')}</button> : null}
                      {profile && !profileEditing ? <button className="gtr2-btn gtr2-btn--sm" onClick={startProfileEdit}>Edit</button> : null}
                      {profileEditing ? <><button className="gtr2-btn gtr2-btn--sm" onClick={() => setProfileEditing(false)}>Cancel</button><button className="gtr2-btn gtr2-btn--sm gtr2-btn--primary" onClick={saveProfile}>Save</button></> : null}
                    </span>
                  </div>
                  {!profile && !profileEditing && (
                    <div className="gtr2-empty">
                      No Role Profile yet. {role.jd_text ? 'Extract one from the job description.' : 'Add a job description first, then extract structured requirements.'}
                    </div>
                  )}
                  {profile && !profileEditing && (
                    <div className="gtrp">
                      {PROFILE_TEXT_KEYS.some(([k]) => profile[k]) ? (
                        <div className="gtrp-facts">
                          {PROFILE_TEXT_KEYS.map(([k, label]) => profile[k] ? (
                            <div key={k} className="gtrp-fact">
                              <div className="gtrp-fact-l">{label}</div>
                              <div className="gtrp-fact-v">{String(profile[k])}</div>
                            </div>
                          ) : null)}
                        </div>
                      ) : null}
                      {PROFILE_LIST_KEYS.map(([k, label]) => {
                        const items = Array.isArray(profile[k]) ? profile[k] : [];
                        if (!items.length) return null;
                        const isReq = k === 'must_haves' || k === 'nice_to_haves';
                        const must = k === 'must_haves';
                        return (
                          <section key={k} className="gtrp-sec">
                            <div className="gtrp-sech">
                              <span className="gtrp-sect">{label}</span>
                              <span className="gtrp-secn">{items.length}</span>
                            </div>
                            {isReq ? (
                              <ul className="gtrp-reqs">
                                {items.map((s, i) => (
                                  <li key={i} className={'gtrp-req ' + (must ? 'gtrp-req--must' : 'gtrp-req--nice')}>
                                    <span className="gtrp-req-m">
                                      {must ? (
                                        <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5l3.2 3.2L13 5" /></svg>
                                      ) : (
                                        <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M8 3.4v9.2M3.4 8h9.2" /></svg>
                                      )}
                                    </span>
                                    <span className="gtrp-req-t">{String(s)}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="gtrp-chips">
                                {items.map((s, i) => (
                                  <span key={i} className={'gtrp-chip' + (k === 'stack' ? ' gtrp-chip--stack' : '')} style={chipTone(s)}>{String(s)}</span>
                                ))}
                              </div>
                            )}
                          </section>
                        );
                      })}
                    </div>
                  )}
                  {profileEditing && (
                    <textarea className="gtr-jsonedit" rows={18} value={profileDraft} aria-label="Role Profile JSON" onChange={e => setProfileDraft(e.target.value)} spellCheck={false} />
                  )}
                </div>
              )}

              {tab === 'Job description' && (
                <div className="gtr2-pad">
                  <div className="gtr2-tabtools">
                    <span>The client's job description, as received.</span>
                    <span className="gtr2-panelacts">
                      {!jdEditing
                        ? <button className="gtr2-btn gtr2-btn--sm" onClick={() => { setJdDraft(role.jd_html || (role.jd_text ? '<p>' + String(role.jd_text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n{2,}/g, '</p><p>').replace(/\n/g, '<br>') + '</p>' : '')); setJdEditing(true); }}>{role.jd_text ? 'Edit' : 'Add JD'}</button>
                        : <><button className="gtr2-btn gtr2-btn--sm" onClick={() => setJdEditing(false)}>Cancel</button><button className="gtr2-btn gtr2-btn--sm gtr2-btn--primary" onClick={saveJd}>Save</button></>}
                    </span>
                  </div>
                  {!jdEditing ? (
                    role.jd_html
                      ? <div className="gtrt-read" dangerouslySetInnerHTML={{ __html: role.jd_html }} />
                      : (role.jd_text
                          ? <pre className="gtr-jd">{role.jd_text}</pre>
                          : <div className="gtr2-empty">No job description on file. Add one to extract a Role Profile.</div>)
                  ) : (
                    <GtEditor value={jdDraft} onChange={setJdDraft} placeholder="Write or paste the job description here." minHeight={340} />
                  )}
                </div>
              )}

              {tab === 'Public advert' && (
                <div className="gtr2-record">
                  <section className="gtr2-panel gtr2-panel--wide">
                    <div className="gtr2-panelh">
                      <h3>Client description</h3>
                      <button className="gtr2-btn gtr2-btn--sm" onClick={generateBlurb} disabled={blurbLoading}><Ico d={ICONS.spark} />{blurbLoading ? 'Generating…' : (blurb ? 'Regenerate' : 'Generate')}</button>
                    </div>
                    <div className="gtr2-hint">Anonymised. Describes the client without naming them, and is reused across every advert this client runs.</div>
                    {blurbMsg && <div className="gtr-note gtr-note-warn">{blurbMsg}</div>}
                    {blurb
                      ? <p className="gtr2-prose">{blurb}</p>
                      : <div className="gtr2-empty">No client description yet. Generate one to open the advert.</div>}
                  </section>

                  <section className="gtr2-panel gtr2-panel--wide">
                    <div className="gtr2-panelh">
                      <h3>Advert body</h3>
                      <button className="gtr2-btn gtr2-btn--sm gtr2-btn--primary" onClick={saveAdvert} disabled={advertSaving}>{advertSaving ? 'Saving…' : 'Save advert'}</button>
                    </div>
                    {advertSeeded && <div className="gtr-note">Started from the job description. Remove anything that must not be public (fees, rate cards, client name) before listing.</div>}
                    <GtEditor value={advertDraft} onChange={setAdvertDraft} placeholder="The public advert. Seeded from the job description; edit down to a public-safe version." minHeight={320} />
                  </section>

                  <section className="gtr2-panel">
                    <div className="gtr2-panelh">
                      <h3>Public details</h3>
                      <button className="gtr2-btn gtr2-btn--sm" onClick={savePublicFields} disabled={pubSaving}>{pubSaving ? 'Saving…' : 'Save details'}</button>
                    </div>
                    <div className="gtr-pubfields">
                      <label><span className="gtr-k">Location</span>
                        <input value={pubFields.public_location} onChange={e => setPubFields({ ...pubFields, public_location: e.target.value })} placeholder="e.g. London or Remote (UK)" /></label>
                      <label><span className="gtr-k">Salary text</span>
                        <input value={pubFields.public_salary_text} onChange={e => setPubFields({ ...pubFields, public_salary_text: e.target.value })} placeholder="e.g. £60,000–£75,000 + benefits" /></label>
                      <label><span className="gtr-k">Work pattern</span>
                        <GtSelect value={pubFields.public_work_pattern} options={WORK_PATTERN_OPTS} onChange={v => setPubFields({ ...pubFields, public_work_pattern: v })} /></label>
                    </div>
                  </section>

                  <section className="gtr2-panel">
                    <div className="gtr2-panelh"><h3>Public listing</h3></div>
                    <ListingBlock role={role} listing={listing} listingBusy={listingBusy} closedRole={closedRole} toggleListing={toggleListing} />
                  </section>
                </div>
              )}

              {tab === 'Rate cards' && (
                <div className="gtr2-pad">
                  {role.placement_model !== 'contract_rate_card' && (
                    <div className="gtr-note">This is a permanent-fee role. Rate cards apply to contract roles, but you can still record bands here.</div>
                  )}
                  {rateCards.length === 0 ? (
                    <div className="gtr2-empty">No rate cards yet.</div>
                  ) : (
                    <div className="gtr2-list">
                      {rateCards.map(rc => (
                        <div key={rc.id} className="gtr2-listrow gtr2-listrow--static">
                          <span className="gtr2-listico"><Ico d={ICONS.rate} /></span>
                          <span className="gtr2-listmain"><b>{rc.band}</b><span>{rc.notes || 'No notes'}</span></span>
                          <span className="gtr2-rate">£{Number(rc.rate_gbp).toLocaleString('en-GB')}<em>/{rc.rate_unit}</em></span>
                          <button className="gtr2-btn gtr2-btn--sm gtr2-btn--danger" onClick={() => deleteRateCard(rc.id)}>Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="gtr-rcadd gtr2-rcadd">
                    <input aria-label="Band" placeholder="Band (e.g. senior)" value={newRc.band} onChange={e => setNewRc({ ...newRc, band: e.target.value })} />
                    <input aria-label="Rate in GBP" placeholder="Rate (GBP)" type="number" value={newRc.rate_gbp} onChange={e => setNewRc({ ...newRc, rate_gbp: e.target.value })} />
                    <GtSelect value={newRc.rate_unit} options={RATE_UNIT_OPTS} onChange={v => setNewRc({ ...newRc, rate_unit: v })} />
                    <input aria-label="Notes" placeholder="Notes (optional)" value={newRc.notes} onChange={e => setNewRc({ ...newRc, notes: e.target.value })} />
                    <button className="gtc-primary" onClick={addRateCard}>Add</button>
                  </div>
                </div>
              )}

              {tab === 'Matches' && (
                <div className="gtr2-pad">
                  <GtSourcingPanel kind="role" ownerId={role.id} getToken={getToken} flash={flash}
                    onOpenCandidate={s => setOpenCandidateId(s.candidate_id)} onChanged={loadSourcing} />
                </div>
              )}

              {tab === 'Interview' && (
                <div className="gtr2-pad">
                  <GtInterviewPlan kind="role" ownerId={role.id} getToken={getToken} flash={flash} />
                </div>
              )}

              {tab === 'Pipeline' && (
                <div className="gtr2-pad">
                  <GtPipeline roleId={roleId} roleDbId={role.id} roleTitle={role.title} authFetch={authFetch} flash={flash} onOpenCandidate={setOpenCandidateId} clientId={role.client_id} getToken={getToken} /* C1-STAGES */ />
                </div>
              )}

              {tab === 'Submissions' && (
                <div className="gtr2-pad">
                  <GtSubmissions roleId={roleId} clientName={role.client_name} authFetch={authFetch} flash={flash} onOpenCandidate={setOpenCandidateId} />
                </div>
              )}

              {tab === 'Notes' && (
                <div className="gtr-notes">
                  <div className="gtr-notelist">
                    {notesHasMore && (
                      <button className="gtr-showmore" onClick={loadEarlierNotes} disabled={notesLoadingMore}>{notesLoadingMore ? 'Loading…' : 'Show earlier'}</button>
                    )}
                    {notes.length === 0 && <div className="gtr2-empty">No notes yet. Add the first one below.</div>}
                    {notes.map(note => {
                      const isMe = myClerkId ? note.author_clerk_id === myClerkId : false;
                      const ac = avatarColour(note.author_name || note.author_clerk_id);
                      return (
                        <div className={'gtr-noterow' + (isMe ? ' gtr-note-me' : '')} key={note.id}>
                          {isMe && userImage
                            ? <img className="gtr-noteav gtr-noteav-img" src={userImage} alt="" />
                            : <span className="gtr-noteav" style={{ background: isMe ? '#1FA9B2' : ac }}>{initials(note.author_name || 'T')}</span>}
                          <div className="gtr-notebody">
                            <div className="gtr-notemeta"><span>{note.author_name || 'Team'}</span><span className="gtr-notetime">{note.created_at ? new Date(note.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span></div>
                            <div className={'gtr-notebubble' + (isMe ? ' gtr-notebubble-me' : '')}>
                              {note.body}
                              <button className="gtr-notedel" title="Delete note" aria-label="Delete note" onClick={() => deleteNote(note.id)}>×</button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="gtr-notecompose">
                    <textarea value={newNote} aria-label="New note" onChange={e => setNewNote(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postNote(); } }}
                      placeholder="Add a note. Enter to send, Shift+Enter for a new line." rows={2} />
                    <button className="gtc-primary" onClick={postNote} disabled={savingNote || !newNote.trim()}>{savingNote ? '…' : 'Send'}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="gtr2-side">
          <div className="gtr-card">
            <div className="gtr-cardh">
              Key information
              {!editing
                ? <button type="button" className="gtr-cardh-edit" onClick={startEdit}>Edit</button>
                : <span className="gtr2-panelacts"><button type="button" className="gtr-cardh-edit gtr2-muted" onClick={() => setEditing(false)}>Cancel</button><button type="button" className="gtr-cardh-edit" onClick={saveEdit}>Save</button></span>}
            </div>
            {!editing ? (
              <div className="gtr-props">
                <Prop k="Client" v={role.client_name || '—'} />
                <Prop k="Contact" v={role.client_contact_name || '—'} />
                <Prop k="Contact email" v={role.client_contact_email ? <a className="gtt-link" href={'mailto:' + role.client_contact_email}>{role.client_contact_email}</a> : '—'} />
                <Prop k="Expertise" v={domainLabel || '—'} />
                <Prop k="Placement" v={isContract ? 'Contract (rate card)' : 'Permanent (fee)'} />
                <Prop k="Fee" v={role.placement_fee_percent != null ? Number(role.placement_fee_percent) + '%' : '—'} />
                <Prop k="Status" v={<span style={{ textTransform: 'capitalize' }}>{role.status}</span>} />
                <Prop k="Created" v={ukDate(role.created_at) || '—'} />
              </div>
            ) : (
              <div className="gtr-editform">
                <label className="gtc-field"><span>Title</span><input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></label>
                <label className="gtc-field"><span>Client</span><ClientField value={form.client_id} onChange={v => setForm({ ...form, client_id: v })} authFetch={authFetch} flash={flash} /></label>
                <label className="gtc-field"><span>Contact name</span><input value={form.client_contact_name} onChange={e => setForm({ ...form, client_contact_name: e.target.value })} /></label>
                <label className="gtc-field"><span>Contact email</span><input value={form.client_contact_email} onChange={e => setForm({ ...form, client_contact_email: e.target.value })} /></label>
                <label className="gtc-field"><span>Expertise domain</span><ExpertiseField value={form.expertise_domain} onChange={v => setForm({ ...form, expertise_domain: v })} authFetch={authFetch} flash={flash} /></label>
                <label className="gtc-field"><span>Status</span><GtSelect value={form.status} options={STATUS_OPTS} onChange={v => setForm({ ...form, status: v })} /></label>
                <label className="gtc-field"><span>Placement model</span><GtSelect value={form.placement_model} options={PLACEMENT_OPTS} onChange={v => setForm({ ...form, placement_model: v })} /></label>
                <label className="gtc-field"><span>Fee percent</span><input type="number" value={form.placement_fee_percent} onChange={e => setForm({ ...form, placement_fee_percent: e.target.value })} /></label>
                <div className="gtr-editactions">
                  <button className="gtc-primary" onClick={saveEdit}>Save changes</button>
                  <button className="gtc-colbtn" onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="gtr-card">
            <div className="gtr-cardh">Public listing <button type="button" className="gtr-cardh-edit" onClick={() => setTab('Public advert')}>Advert</button></div>
            <ListingBlock role={role} listing={listing} listingBusy={listingBusy} closedRole={closedRole} toggleListing={toggleListing} compact />
          </div>

          {profile && PROFILE_TEXT_KEYS.some(([k]) => profile[k]) && (
            <div className="gtr-card">
              <div className="gtr-cardh">At a glance <button type="button" className="gtr-cardh-edit" onClick={() => setTab('Profile')}>Profile</button></div>
              <div className="gtr-props">
                {PROFILE_TEXT_KEYS.map(([k, label]) => profile[k] ? <Prop key={k} k={label} v={String(profile[k])} /> : null)}
                <Prop k="Must-haves" v={mustN} />
                <Prop k="Skills" v={skillN} />
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Prop({ k, v }) { return <div className="gtr-prop"><span className="gtr-prop-k">{k}</span><span className="gtr-prop-v">{v}</span></div>; }

// The listing switch appears in the side card and on the advert tab, so it is
// one component: a filled or closed role cannot be listed, whichever place asks.
function ListingBlock({ role, listing, listingBusy, closedRole, toggleListing, compact }) {
  if (closedRole) {
    return <div className="gtr-note gtr-note-warn">This role is {role.status}. A {role.status} role cannot be listed publicly, and is taken off the listing automatically when its status changes.</div>;
  }
  return (
    <div className={'gtr2-listing' + (compact ? ' gtr2-listing--compact' : '')}>
      <div className="gtr2-listing-state">
        <span className={'gtr2-livedot' + (listing ? '' : ' gtr2-livedot--off')} />
        <span>
          <b>{listing ? 'Live on the job board' : 'Not on the job board'}</b>
          {listing && role.ref ? <em>jobs.gtco.io/{role.ref}</em> : <em>Candidates cannot find or apply to it</em>}
        </span>
      </div>
      <button className={'gtr2-btn gtr2-btn--sm' + (listing ? '' : ' gtr2-btn--primary')} onClick={() => toggleListing(!listing)} disabled={listingBusy}>
        {listingBusy ? 'Working…' : (listing ? 'Remove from listing' : 'List publicly')}
      </button>
    </div>
  );
}
